import { useMemo, useState } from 'react'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { DepositSplitRow } from './deposit-split.types'
import { keysOf } from 'src/utils/utils'
import { useTranslation } from 'react-i18next'
import { ActionsCell, ReferenceDescriptionCell } from './deposit-split-utils'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { Link, Stack } from '@vapor/material'
import CheckoutDocumentForm from '../../../checkout-document-form'
import { Typography } from '@mui/material'

type DepositSplitGridProps = {
  propertyId: string
  data: DepositSplitRow[]
  usedQuotas: DepositSplitRow[]
  edit: (id: string) => void
  split: (id: string) => void
  deleteFn: (id: string) => void
  loading: boolean
}

const field = keysOf<DepositSplitRow>()
export const DepositSplitGrid = (props: DepositSplitGridProps) => {
  const { data, edit, split, deleteFn } = props
  const { t } = useTranslation()
  const { formatTableCurrency } = useFormatCurrency()
  const { currency } = useTenant()
  const TOTAL = 'TOTAL'

  const showDelete = useMemo(() => data.length > 1, [data.length])

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)

  const onDocumentClick = (documentId: string) => {
    setSelectedDocumentId(documentId)
  }

  const commonColumns: GridColDef<DepositSplitRow>[] = [
    {
      field: 'referenceDescription',
      headerName: t('pms.accounting.documents.deposit_split.reference'),
      renderCell: (params) => {
        const { row } = params
        if (row.id === TOTAL) return <b>{t('common.total')}</b>
        else return <ReferenceDescriptionCell type={row.reference.type} description={row.reference.description} />
      },
      flex: 1,
    },
    {
      field: field('amount'),
      headerName: t('pms.accounting.documents.deposit_split.amount'),
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      type: 'number',
      renderCell: (params) => {
        const { row, value } = params
        if (row.id === TOTAL) return <b>{formatTableCurrency(value ?? 0, currency)}</b>
        else return formatTableCurrency(value ?? 0, currency)
      },
    },
  ]

  const actionColumn: GridColDef<DepositSplitRow> = {
    field: 'action',
    headerName: '',
    type: 'action',
    minWidth: 100,
    maxWidth: 150,
    flex: 1,
    renderCell: (params) => {
      const { row } = params
      const showEdit = Boolean(!row.documentReferenceId)
      const showSplitOnRow = Boolean(!row.documentReferenceId)
      const showDeleteOnRow = showDelete && Boolean(!row.documentReferenceId)
      const splitFn = showSplitOnRow
        ? () => {
            split(row.id)
          }
        : undefined

      if (row.id === TOTAL) return <></>
      else
        return (
          <ActionsCell
            showEdit={showEdit}
            edit={() => {
              edit(row.id)
            }}
            showSplit={showSplitOnRow}
            split={splitFn}
            showDelete={showDeleteOnRow}
            deleteFn={() => deleteFn(row.id)}
          />
        )
    },
  }

  const documentReferenceColumn: GridColDef<DepositSplitRow> = {
    field: field('documentReferenceDescription'),
    headerName: t('pms.accounting.documents.deposit_split.documentReference'),
    flex: 1,
    minWidth: 100,
    maxWidth: 200,
    renderCell: (params) => {
      const { row } = params
      if (row.documentReferenceId) {
        return <Link onClick={() => (row.documentReferenceId ? onDocumentClick(row.documentReferenceId) : undefined)}>{params.formattedValue}</Link>
      }
      return ''
    },
  }

  const totalAmount = data.reduce((p, c) => p + c.amount, 0)
  const rows: DepositSplitRow[] = data.length ? [...data, { id: TOTAL, currency: data?.[0].currency ?? currency, reference: { type: 'bill', description: '' }, amount: totalAmount }] : []

  return (
    <Stack spacing={2}>
      <DataGrid columns={[...commonColumns, actionColumn]} rows={rows} hideFooter loading={props.loading} autoHeight />
      <Typography variant="titleXSmall">
        {t('pms.common.checkout_document.amounts.deposit_assigneds_document_title', {
          count: props.usedQuotas.length,
        })}
      </Typography>
      <DataGrid columns={[...commonColumns, documentReferenceColumn]} rows={props.usedQuotas} hideFooter loading={props.loading} sx={{ height: 300 }} autoHeight />
      {selectedDocumentId !== null && (
        <CheckoutDocumentForm //
          propertyId={props.propertyId}
          open={selectedDocumentId !== null}
          onClose={() => setSelectedDocumentId(null)}
          documentId={selectedDocumentId}
          onOpenReferenceDocument={(documentId: string) => setSelectedDocumentId(documentId)}
          onCreditNoteSuccess={(creditNoteId, manualDocumentId) => setSelectedDocumentId(manualDocumentId ? manualDocumentId : creditNoteId)}
          refetchQueries={['Documents']}
          onSuccess={({ documentId }) => {
            setSelectedDocumentId(null)
          }}
        />
      )}
    </Stack>
  )
}
